import { useMutation, useQuery } from '@tanstack/react-query'
import { type FormEvent, useState } from 'react'
import { type FleetAnswer, RequestError, api } from '../../lib/api'

/**
 * The Afra-side intelligence surface.
 *
 * The question goes to a planner that may only name primitives; the answer is
 * narrated from what those primitives returned. The raw calls are shown beneath
 * every answer on purpose — an analytic surface nobody can check is one nobody
 * should trust, and the sample size is what makes each number readable at all.
 */
export function IntelligencePage() {
  const [question, setQuestion] = useState('')
  const [answer, setAnswer] = useState<FleetAnswer | null>(null)
  const [error, setError] = useState<string | null>(null)

  const primitives = useQuery({ queryKey: ['fleet-primitives'], queryFn: api.fleetPrimitives })
  const gaps = useQuery({ queryKey: ['knowledge-gaps'], queryFn: api.knowledgeGaps })

  const ask = useMutation({
    mutationFn: (q: string) => api.fleetQuery(q),
    onSuccess: (result) => {
      setAnswer(result)
      setError(null)
    },
    onError: (err) => setError(err instanceof RequestError ? err.messageFa : 'پرسش پاسخ نگرفت.'),
  })

  const submit = (event: FormEvent) => {
    event.preventDefault()
    if (question.trim().length >= 3) ask.mutate(question.trim())
  }

  return (
    <div className="container stack">
      <div className="card">
        <h1>هوشمندی شبکه</h1>
        <p className="meta">
          پرسش‌تان را به فارسی بنویسید. پاسخ از مجموعه‌ای ثابت و قابل ممیزی از توابع آماری ساخته
          می‌شود — مدل هیچ‌وقت به داده خام مؤسسات دسترسی ندارد و SQL نمی‌نویسد. هر خانه‌ای که کمتر
          از پنج مؤسسه داشته باشد اصلاً ذخیره نمی‌شود.
        </p>

        <form onSubmit={submit}>
          <div className="field">
            <input
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              placeholder="مثلاً: بیشتر مؤسسات در کدام بخش ضعیف‌اند؟"
            />
          </div>
          <button type="submit" className="btn" disabled={ask.isPending}>
            {ask.isPending ? 'در حال محاسبه…' : 'بپرس'}
          </button>
        </form>

        {error && <div className="notice notice--error" style={{ marginTop: 'var(--space-3)' }}>{error}</div>}
      </div>

      {answer && (
        <div className="card">
          <h2>پاسخ</h2>
          {answer.degraded && (
            <div className="notice notice--info">
              این پاسخ کامل تولید نشد؛ اعداد خام همچنان معتبرند.
            </div>
          )}
          <p style={{ whiteSpace: 'pre-wrap' }}>{answer.narrationFa}</p>
          {answer.model && (
            <span className="badge badge--generated" title={`نوشته‌شده توسط ${answer.model}`}>
              متن نوشته هوش مصنوعی
            </span>
          )}

          {answer.calls.map((call, i) => (
            <div key={`${call.primitive}-${i}`} style={{ marginTop: 'var(--space-5)' }}>
              <div className="row" style={{ justifyContent: 'space-between' }}>
                <span className="badge">{call.primitive}</span>
                <span className="meta">
                  n = <span className="num">{call.sampleSize}</span>
                </span>
              </div>
              {call.suppressedFa ? (
                <div className="notice notice--info">{call.suppressedFa}</div>
              ) : (
                <Table rows={call.rows} />
              )}
            </div>
          ))}
        </div>
      )}

      <div className="card">
        <h2>صف شکاف‌های دانش</h2>
        <p className="meta">
          جاهایی که افرا پاسخی نداشت، به ترتیب اینکه چند مؤسسه به همان دیوار خورده‌اند. این فهرست،
          نقشه راه محتوای افراست: ندانستنِ دستیار، سرفصل درس است.
        </p>
        {(gaps.data?.gaps.length ?? 0) === 0 ? (
          <p className="meta">فعلاً چیزی در صف نیست.</p>
        ) : (
          (gaps.data?.gaps ?? []).slice(0, 15).map((gap, i) => (
            <div
              key={`${gap.statementCode ?? 'none'}-${i}`}
              style={{ padding: 'var(--space-3) 0', borderTop: '1px solid var(--line)' }}
            >
              <div style={{ lineHeight: 1.9 }}>{gap.questionFa}</div>
              <div className="meta">
                {gap.statementCode && <>گزاره {gap.statementCode} · </>}
                <span className="num">{gap.organizations}</span> مؤسسه ·{' '}
                <span className="num">{gap.occurrences}</span> بار
              </div>
            </div>
          ))
        )}
      </div>

      <div className="card">
        <h2>توابع در دسترس</h2>
        {(primitives.data?.primitives ?? []).map((primitive) => (
          <div key={primitive.name} style={{ padding: 'var(--space-2) 0' }}>
            <span className="badge">{primitive.name}</span>{' '}
            <span className="meta">{primitive.descriptionFa}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

function Table({ rows }: { rows: Record<string, unknown>[] }) {
  if (rows.length === 0) return <p className="meta">نتیجه‌ای نداشت.</p>
  const columns = Object.keys(rows[0] ?? {})

  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--text-sm)' }}>
        <thead>
          <tr>
            {columns.map((column) => (
              <th
                key={column}
                style={{ textAlign: 'start', padding: '4px 8px', borderBottom: '1px solid var(--line)' }}
              >
                {column}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.slice(0, 20).map((row, i) => (
            <tr key={i}>
              {columns.map((column) => {
                const value = row[column]
                const isNumber = typeof value === 'number'
                return (
                  <td
                    key={column}
                    style={{ padding: '4px 8px', borderBottom: '1px solid var(--line)', lineHeight: 1.8 }}
                  >
                    <span className={isNumber ? 'num' : undefined}>
                      {value === null || value === undefined ? '—' : String(value).slice(0, 90)}
                    </span>
                  </td>
                )
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
